import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // Base styles - mobile-first with proper touch targets
  // Width is controlled separately via fullWidth variant to avoid conflicts with flex layouts
  // Tactile polish (Feb 2026): subtle active scale + transition so primary/secondary
  // buttons feel like iOS controls instead of web buttons. Pure CSS, no JS.
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-[transform,background-color,box-shadow,color] duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-card hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-card hover:bg-destructive/90",
        outline:
          "border border-gray-200 bg-white text-gray-900 shadow-tile hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300",
        secondary:
          "bg-secondary text-secondary-foreground shadow-tile hover:bg-secondary/80",
        ghost: "hover:bg-accent/10 hover:text-gray-900",
        link: "text-primary underline-offset-4 hover:underline active:scale-100",
      },
      size: {
        // Mobile-first: 44-48px min touch target, desktop: standard heights
        default: "min-h-[44px] px-4 py-3 sm:min-h-0 sm:h-9 sm:py-2",
        sm: "min-h-[40px] rounded-md px-3 text-xs sm:min-h-0 sm:h-8",
        lg: "min-h-[48px] rounded-md px-8 sm:min-h-0 sm:h-10",
        icon: "h-12 w-12 sm:h-9 sm:w-9",
      },
      // Width control - respects flex layouts when className contains flex-1
      fullWidth: {
        true: "w-full",
        false: "w-auto whitespace-nowrap",
        // Default: full-width on mobile for better touch, auto on desktop
        mobile: "w-full sm:w-auto sm:whitespace-nowrap",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      fullWidth: false,
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, fullWidth, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  
  // Auto-detect if button is in a flex layout (has flex-1 or flex-auto in className)
  // In that case, don't apply default fullWidth behavior
  const isFlexChild = className && (className.includes('flex-1') || className.includes('flex-auto'))
  const effectiveFullWidth = isFlexChild ? false : fullWidth
  
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, fullWidth: effectiveFullWidth, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
